import type { ReactNode } from "react";
import { useState } from "react";
import className from "~/kernel/styling/className";
import isPresent from "~/kernel/validation/isPresent";
import Icon from "./Icon";

type SubtleAccordionProps = {
  summary: ReactNode;
  children: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onClick?: () => void;
};

export default function SubtleAccordion({
  summary,
  children,
  open,
  onClick,
  defaultOpen,
}: SubtleAccordionProps) {
  const isControlled = isPresent(open);

  if (isControlled) {
    return (
      <ControlledSubtleAccordion
        open={open}
        onClick={onClick}
        summary={summary}
        children={children}
      />
    );
  }

  return (
    <UncontrolledSubtleAccordion
      defaultOpen={defaultOpen}
      summary={summary}
      children={children}
    />
  );
}

type UncontrolledSubtleAccordionProps = {
  summary: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
};

function UncontrolledSubtleAccordion({
  summary,
  children,
  defaultOpen,
}: UncontrolledSubtleAccordionProps) {
  const [open, setOpen] = useState(defaultOpen ?? true);
  return (
    <ControlledSubtleAccordion
      summary={summary}
      children={children}
      open={open}
      onClick={() => setOpen(!open)}
    />
  );
}

type ControlledSubtleAccordionProps = {
  open: boolean;
  onClick?: () => void;
  summary: ReactNode;
  children: ReactNode;
};

function ControlledSubtleAccordion({
  summary,
  children,
  open,
  onClick,
}: ControlledSubtleAccordionProps) {
  return (
    <div>
      <button
        type="button"
        className={className(
          "flex w-full items-center justify-between py-2 text-left text-slate-600 group",
          { "border-b border-b-slate-200": open },
        )}
        onClick={onClick}
      >
        <div className="group-hover:underline">{summary}</div>

        <Icon
          name="chevron-down"
          className={className("transition-transform", {
            "rotate-180": open,
          })}
        />
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}
